import React from "react";
import { useNavigate } from "react-router";
import { useToast } from "../components/ResultsPrimitives.jsx";
import { invalidatePageDataCacheEntries } from "./usePageData.js";
import { invalidateShellDataCache } from "./useShellData.js";
import { clearAppStatusNoticeBySource } from "../utils/appStatusNotice.js";
import {
  buildImportLoadingToastContent,
  buildPendingImportSubmissionState,
  createInitialImportSubmissionState,
  IMPORT_INVALIDATED_API_URLS,
} from "../utils/importFilesPageState.js";
import {
  createToastProgress,
  createToastProgressReporter,
  IMPORT_PROGRESS_STEPS,
  IMPORT_TOAST_PANEL_CLASS,
} from "../utils/toastProgress.js";
import { submitImportRequest } from "../validation/import/submitImportRequest.js";

// The page scrolls inside AppShell on desktop and in the document on mobile.
// scrollIntoView respects both ancestors and the measured bottom clearance.
function useImportConfirmationViewport({ isPaused, actionsRef }) {
  const [bottomSpace, setBottomSpace] = React.useState(0);
  const frameRef = React.useRef(null);
  const isPausedRef = React.useRef(isPaused);
  isPausedRef.current = isPaused;

  const revealActions = React.useCallback((focus = false) => {
    const actions = actionsRef?.current;
    const stack = document.querySelector('[data-react-toast-stack="true"]');
    if (!isPausedRef.current || !actions || !stack) return;
    const clearance = Math.max(0, Math.ceil(window.innerHeight - stack.getBoundingClientRect().top)) + 16;
    setBottomSpace(clearance);
    actions.style.scrollMarginBottom = `${clearance}px`;
    window.cancelAnimationFrame(frameRef.current);
    frameRef.current = window.requestAnimationFrame(() => {
      actions.scrollIntoView({ behavior: "instant", block: "end" });
      if (focus) actions.focus({ preventScroll: true });
    });
  }, [actionsRef]);

  React.useLayoutEffect(() => {
    if (!isPaused) return undefined;
    const actions = actionsRef?.current;
    const stack = document.querySelector('[data-react-toast-stack="true"]');
    const update = () => revealActions();
    const observer = new ResizeObserver(update);
    if (stack) observer.observe(stack);
    if (actions) observer.observe(actions);
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    update();
    return () => {
      observer.disconnect();
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.cancelAnimationFrame(frameRef.current);
      actions?.style.removeProperty("scroll-margin-bottom");
    };
  }, [actionsRef, isPaused, revealActions]);

  const reviewImportChanges = React.useCallback(() => revealActions(true), [revealActions]);
  return { confirmationBottomSpace: isPaused ? bottomSpace : 0, reviewImportChanges };
}

function resolveImportFeedbackMessage(feedback) {
  return typeof feedback?.message === "string" && feedback.message.trim()
    ? feedback.message.trim()
    : "";
}

function resolveImportLoadingToastResolution({
  dismissToast,
  loadingToastContent,
  loadingToastId,
  nextSubmissionState,
  navigate,
  onReviewImport,
  refreshError,
  updateToast,
}) {
  if (!loadingToastId) {
    return true;
  }

  const { completionNotice, feedback, results, status } = nextSubmissionState;
  const feedbackMessage = resolveImportFeedbackMessage(feedback);
  const hasAppliedFiles = results.some((result) => result?.status === "success");
  const hasFailedFiles = results.some((result) => result?.status === "error");
  const refreshMessage = refreshError
    ? "L’actualisation de l’affichage a échoué. Rafraîchissez la page; les imports enregistrés sont conservés."
    : "";
  const resultAction = completionNotice?.resultUrl ? {
    actionLabel: completionNotice.resultActionLabel,
    onAction: () => navigate(completionNotice.resultUrl),
  } : {};

  if (status === "confirmation-required") {
    updateToast(loadingToastId, {
      type: "paused",
      title: "Confirmation requise",
      message: "Vérifiez les changements, puis confirmez pour poursuivre.",
      duration: null,
      panelClassName: IMPORT_TOAST_PANEL_CLASS,
      actionLabel: "Voir et confirmer",
      onAction: onReviewImport,
      dismissOnAction: false,
    });
    return true;
  }

  if (status === "success" && !hasFailedFiles) {
    updateToast(loadingToastId, {
      duration: refreshError ? 9000 : 6000,
      message: [
        feedbackMessage || completionNotice?.message || "La mise à jour des sources sélectionnées est terminée.",
        loadingToastContent.title === "Remplacement en cours" ? completionNotice?.message : "",
        refreshMessage,
      ].filter(Boolean).join(" "),
      title: loadingToastContent.title === "Remplacement en cours"
        ? "Remplacement terminé"
        : "Import terminé",
      type: refreshError || feedback?.tone !== "success" ? "info" : "success",
      panelClassName: IMPORT_TOAST_PANEL_CLASS,
      ...resultAction,
    });
    return true;
  }

  if (hasFailedFiles || ["backend-error", "network-error", "session-error", "validation-error"].includes(status)) {
    updateToast(loadingToastId, {
      duration: refreshError ? 9000 : 8000,
      message: [
        hasFailedFiles && status === "success"
          ? "Un ou plusieurs fichiers ont été refusés. Vérifiez le détail dans la page Imports."
          : feedbackMessage || "L'import a échoué. Vérifiez le détail dans la page Imports.",
        hasFailedFiles && status === "success" && feedback?.issue?.code !== "import.completed" ? feedbackMessage : "",
        hasAppliedFiles ? "Les fichiers importés avec succès sont conservés." : "",
        refreshMessage,
      ].filter(Boolean).join(" "),
      title: hasAppliedFiles ? "Import partiellement terminé" : loadingToastContent.errorTitle,
      type: "error",
      panelClassName: IMPORT_TOAST_PANEL_CLASS,
      ...resultAction,
    });
    return true;
  }

  dismissToast(loadingToastId);
  return true;
}

export function useImportSubmissionFlow({
  bypassOrderImportGuards = false,
  fieldConfigs,
  filesByField,
  confirmationActionsRef,
  refreshImportHistory,
  resetAllSelectedFiles,
  resolveResultLabel,
  selectedFileCount,
}) {
  const { dismissToast, showToast, updateToast } = useToast();
  const navigate = useNavigate();
  const submissionLockRef = React.useRef(false);
  const operationRef = React.useRef(null);
  const isMountedRef = React.useRef(true);
  const [isSubmitting, setIsSubmitting] = React.useState(false);
  const [activeSubmissionMode, setActiveSubmissionMode] = React.useState(null);
  const [submissionState, setSubmissionState] = React.useState(() => createInitialImportSubmissionState());

  const feedback = submissionState.feedback;
  const impact = submissionState.impact;
  const results = submissionState.results;
  const hasServerStatus = Boolean(feedback || results.length > 0);
  const { confirmationBottomSpace, reviewImportChanges } = useImportConfirmationViewport({
    isPaused: submissionState.status === "confirmation-required" && !isSubmitting,
    actionsRef: confirmationActionsRef,
  });

  const clearImportOperation = React.useCallback(() => {
    const operation = operationRef.current;
    operation?.reportProgress.finish({ status: "skipped" });
    if (operation?.toastId) dismissToast(operation.toastId);
    operationRef.current = null;
  }, [dismissToast]);

  React.useEffect(() => {
    isMountedRef.current = true;
    return () => {
      isMountedRef.current = false;
      clearImportOperation();
    };
  }, [clearImportOperation]);

  const clearServerStatus = React.useCallback(() => {
    clearImportOperation();
    setSubmissionState(createInitialImportSubmissionState());
  }, [clearImportOperation]);

  const submitImport = React.useCallback(async ({ confirmBreakingImport = false, submissionMode = "selection" } = {}) => {
    if (submissionLockRef.current || isSubmitting) {
      return null;
    }

    submissionLockRef.current = true;
    const resumedOperation = confirmBreakingImport && operationRef.current?.reportProgress.getSnapshot()?.pausedAt != null
      ? operationRef.current
      : null;
    if (!resumedOperation) clearImportOperation();

    const shouldTrackPendingSubmission = selectedFileCount > 0;
    const loadingToastContent = buildImportLoadingToastContent({ submissionMode });
    const progress = resumedOperation?.reportProgress.getSnapshot()
      || (shouldTrackPendingSubmission ? createToastProgress(IMPORT_PROGRESS_STEPS) : null);
    const loadingToast = {
      duration: null,
      message: loadingToastContent.message,
      title: loadingToastContent.title,
      type: "loading",
      panelClassName: IMPORT_TOAST_PANEL_CLASS,
      actionLabel: null,
      onAction: null,
      dismissOnAction: true,
      progress,
    };
    const loadingToastId = resumedOperation?.toastId || (shouldTrackPendingSubmission ? showToast(loadingToast) : null);
    if (resumedOperation) updateToast(loadingToastId, loadingToast);
    const reportProgress = resumedOperation?.reportProgress || createToastProgressReporter(progress, (nextProgress) => {
      updateToast(loadingToastId, { progress: nextProgress });
    });
    const operation = resumedOperation || { toastId: loadingToastId, reportProgress };
    operationRef.current = operation;
    if (resumedOperation) reportProgress.resume();
    let hasResolvedLoadingToast = false;

    if (shouldTrackPendingSubmission) {
      clearAppStatusNoticeBySource("import");
      setIsSubmitting(true);
      setActiveSubmissionMode(submissionMode);
      setSubmissionState(buildPendingImportSubmissionState());
    }

    try {
      const nextSubmissionState = await submitImportRequest({
        bypassOrderImportGuards,
        confirmBreakingImport,
        fieldConfigs,
        filesByField,
        previousState: submissionState,
        onDuplicateConfirmationChange: (paused) => {
          if (paused) {
            const activeStep = reportProgress.getSnapshot()?.steps.find((step) => step.status === "running");
            reportProgress.pause(activeStep?.key);
            updateToast(loadingToastId, { type: "paused", title: "Doublons à vérifier", message: "L’import attend votre décision dans la fenêtre de confirmation." });
          } else {
            reportProgress.resume();
            updateToast(loadingToastId, { type: "loading", ...loadingToastContent });
          }
        },
        reportProgress: () => {
          // The first POST combines analysis and any immediate writes. Only a
          // confirmed second POST exposes a separate observed save phase.
          if (resumedOperation) reportProgress("save");
        },
        resolveResultLabel,
        selectedFileCount,
        submissionMode,
      });

      if (!isMountedRef.current || operationRef.current !== operation) return nextSubmissionState;
      setSubmissionState(nextSubmissionState);
      if (nextSubmissionState.status === "cancelled") {
        reportProgress.finish({ status: "skipped" });
        dismissToast(loadingToastId);
        hasResolvedLoadingToast = true;
        return nextSubmissionState;
      }
      if (nextSubmissionState.status === "confirmation-required") {
        const activeStep = reportProgress.getSnapshot()?.steps.find((step) => step.status === "running");
        reportProgress.pause(activeStep?.key);
        hasResolvedLoadingToast = resolveImportLoadingToastResolution({
          dismissToast, loadingToastContent, loadingToastId, nextSubmissionState,
          navigate, onReviewImport: reviewImportChanges, updateToast,
        });
        return nextSubmissionState;
      }
      const canRefresh = nextSubmissionState.status !== "confirmation-required"
        && (nextSubmissionState.status === "success" || nextSubmissionState.shouldInvalidateCache);
      const hasImportError = nextSubmissionState.status !== "success"
        || nextSubmissionState.results.some((result) => result?.status === "error");
      let refreshError = null;
      if (canRefresh) {
        if (!resumedOperation) reportProgress("analyze", "completed");
        reportProgress("save", nextSubmissionState.shouldInvalidateCache ? (hasImportError ? "warning" : "completed") : "skipped");
        reportProgress("refresh");
        updateToast(loadingToastId, { message: "Traitement terminé. Actualisation de l’affichage en cours…" });
      }
      try {
        if (nextSubmissionState.shouldInvalidateCache && canRefresh) {
          invalidatePageDataCacheEntries(IMPORT_INVALIDATED_API_URLS);
          invalidateShellDataCache();
        }
        if (nextSubmissionState.shouldResetSelection) resetAllSelectedFiles();
        if (nextSubmissionState.shouldInvalidateCache && canRefresh) await refreshImportHistory?.();
        if (canRefresh) reportProgress("refresh", "completed");
      } catch (error) {
        refreshError = error;
        reportProgress("refresh", "failed");
      }
      reportProgress.finish({ status: "failed" });
      hasResolvedLoadingToast = resolveImportLoadingToastResolution({
        dismissToast,
        loadingToastContent,
        loadingToastId,
        nextSubmissionState,
        navigate,
        onReviewImport: reviewImportChanges,
        refreshError,
        updateToast,
      });

      return nextSubmissionState;
    } finally {
      if (reportProgress.getSnapshot()?.pausedAt == null || !hasResolvedLoadingToast) {
        reportProgress.finish({ status: "failed" });
        if (operationRef.current === operation) operationRef.current = null;
      }
      if (loadingToastId && !hasResolvedLoadingToast) {
        dismissToast(loadingToastId);
      }

      submissionLockRef.current = false;
      if (shouldTrackPendingSubmission) {
        setIsSubmitting(false);
        setActiveSubmissionMode(null);
      }
    }
  }, [
    bypassOrderImportGuards,
    clearImportOperation,
    dismissToast,
    fieldConfigs,
    filesByField,
    isSubmitting,
    navigate,
    reviewImportChanges,
    refreshImportHistory,
    resetAllSelectedFiles,
    resolveResultLabel,
    selectedFileCount,
    showToast,
    submissionState,
    updateToast,
  ]);

  return {
    activeSubmissionMode,
    confirmationBottomSpace,
    clearServerStatus,
    feedback,
    hasServerStatus,
    impact,
    isSubmitting,
    results,
    submissionMode: submissionState.submissionMode,
    submitImport,
  };
}
